<template>
  <v-sheet color="surface" rounded="lg" class="pa-4 appeal-section">
    <v-container :fluid="true">
      <div v-if="errorMsg" class="text-error mb-2">{{ errorMsg }}</div>

      <v-alert v-if="sentInfo" type="success" variant="tonal" density="compact" class="mb-4">
        Appeal sent {{ sentInfo.sentAtLabel }}<span v-if="sentInfo.sentByName"> by {{ sentInfo.sentByName }}</span
        >.
        <a v-if="sentInfo.sentDocumentUrl" :href="sentInfo.sentDocumentUrl" target="_blank" class="text-primary">
          View sent document
        </a>
      </v-alert>

      <!-- The order's own documents auto-attached to the fax (payer response, patient chart, medical
           necessity). The user sees what goes out and can remove any before sending. -->
      <div
        v-if="autoEnclosures.length || packetReviewPending || packetReviewError"
        class="appeal-section__enclosures mb-4">
        <div
          v-if="autoEnclosures.length"
          class="text-caption text-medium-emphasis mb-1 d-flex align-center flex-wrap ga-1">
          <span class="d-flex align-center">
            <v-icon size="small" class="mr-1">mdi-paperclip</v-icon>
            <span v-if="visibleEnclosures.length">
              {{ visibleEnclosures.length }} document{{ visibleEnclosures.length === 1 ? "" : "s" }} from this order
              will be attached to the appeal &mdash; click one to view or remove pages:
            </span>
            <span v-else>No order documents will be attached &mdash; only the letter will be sent.</span>
          </span>
          <a v-if="removedCount" class="text-primary" style="cursor: pointer" @click="restoreEnclosures">
            Restore removed ({{ removedCount }})
          </a>
        </div>
        <div v-if="visibleEnclosures.length" class="d-flex flex-wrap ga-1">
          <v-chip
            v-for="enc in visibleEnclosures"
            :key="enc.identity"
            size="small"
            :variant="isEnclosureFlagged(enc.identity) ? 'flat' : 'tonal'"
            :color="isEnclosureFlagged(enc.identity) ? 'warning' : 'primary'"
            label
            link
            closable
            @click="openPagesDialog(enc)"
            @click:close="excludeEnclosure(enc.identity)">
            <v-icon v-if="isEnclosureFlagged(enc.identity)" start size="small">mdi-alert</v-icon>
            {{ enc.label }}{{ removedPagesSuffix(enc.identity) }}
          </v-chip>
        </div>
        <div v-if="packetReviewPending" class="text-caption text-medium-emphasis mt-1 d-flex align-center ga-1">
          <v-progress-circular indeterminate size="12" width="2" color="primary" />
          <span>Checking the packet for blank payer forms and missing documents&hellip;</span>
        </div>
        <div v-else-if="packetReviewError" class="text-caption text-warning mt-1">{{ packetReviewError }}</div>
      </div>

      <v-dialog v-model="pagesDialogOpen" max-width="900">
        <v-card v-if="pagesDialogEnclosure">
          <v-card-title class="d-flex align-center justify-space-between">
            <span class="text-subtitle-1">{{ pagesDialogEnclosure.label }}</span>
            <v-btn icon="mdi-close" variant="text" size="small" @click="pagesDialogOpen = false" />
          </v-card-title>
          <v-card-text class="pt-0">
            <div v-if="pagesLoading" class="d-flex justify-center pa-8">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <div v-else-if="pagesError" class="text-error">{{ pagesError }}</div>
            <template v-else-if="pagesDialogInfo">
              <div class="text-caption text-medium-emphasis mb-2 d-flex align-center flex-wrap ga-1">
                <span
                  >Click a page number to remove it from the fax. Removed pages are skipped when the appeal is
                  sent.</span
                >
                <a
                  v-if="dialogExcludedPages.length"
                  class="text-primary"
                  style="cursor: pointer"
                  @click="restoreDialogPages">
                  Restore all pages
                </a>
              </div>
              <v-alert
                v-for="(flag, i) in dialogFlags"
                :key="i"
                type="warning"
                variant="tonal"
                density="compact"
                class="mb-2">
                {{ flag.message }}
              </v-alert>
              <div class="d-flex flex-wrap ga-1 mb-3">
                <v-chip
                  v-for="page in pagesDialogInfo.pageCount"
                  :key="page"
                  size="small"
                  label
                  :variant="isPageRemoved(page) || isPageFlagged(page) ? 'flat' : 'tonal'"
                  :color="isPageRemoved(page) ? 'error' : isPageFlagged(page) ? 'warning' : 'primary'"
                  @click="togglePage(page)">
                  <v-icon v-if="isPageFlagged(page)" start size="small">mdi-alert</v-icon>
                  {{ isPageRemoved(page) ? `Page ${page} — removed` : `Page ${page}` }}
                </v-chip>
              </div>
              <iframe
                :src="pagesDialogInfo.viewUrl"
                class="appeal-section__enclosure-frame"
                title="Enclosure preview" />
            </template>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- No draft yet: intro + optional attachments + generate -->
      <template v-if="!hasDraft">
        <p class="appeal-section__intro mb-4">
          Generate a draft appeal letter from this patient's record and the denial reason. You can optionally attach
          additional patient documents or clinical guidelines to strengthen the appeal.
        </p>
        <div class="d-flex flex-wrap align-center mb-4">
          <div v-for="(doc, i) in documentUrls" :key="doc.link" class="d-flex align-center mr-2 mb-1">
            <v-icon size="small" class="mr-1">mdi-paperclip</v-icon>
            <span class="text-body-2">{{ doc.fileName || "Document" }}</span>
            <v-btn icon="mdi-close" variant="text" size="x-small" @click="removeDocument(i)" />
          </div>
          <v-btn
            tag="label"
            variant="tonal"
            color="primary"
            rounded
            size="small"
            prepend-icon="mdi-paperclip"
            :loading="uploading"
            :disabled="generating">
            Attach a document
            <input type="file" accept=".pdf,.tif,.tiff,.png,.jpg,.jpeg" class="d-none" @change="onPickFile" />
          </v-btn>
        </div>
        <v-btn
          color="primary"
          variant="flat"
          rounded
          prepend-icon="mdi-creation"
          :loading="generating"
          @click="onGenerate">
          Generate appeal letter
        </v-btn>
      </template>

      <!-- Draft exists: fax/email choice (same flag and eFax rule as send-to-plan), then the chosen
           editor. Either editor edits the letter in its own body field, and that body is what Send
           ships inside the packet and what the backend persists as the draft. -->
      <template v-else>
        <v-btn-toggle
          v-if="sendEmailEnabled && order.submissionMethod !== 'eFax'"
          v-model="sendMethod"
          mandatory
          class="mb-4"
          color="primary"
          variant="outlined">
          <v-btn value="fax">
            <v-icon start>mdi-fax</v-icon>
            Send Fax
          </v-btn>
          <v-btn value="email">
            <v-icon start>mdi-email</v-icon>
            Send Email
          </v-btn>
        </v-btn-toggle>

        <v-dialog v-model="coverDialogOpen" max-width="720">
          <v-card>
            <v-card-title class="text-subtitle-1">New cover sheet template</v-card-title>
            <v-card-text>
              <v-text-field v-model="coverForm.name" label="Template name" class="mb-3" />
              <v-btn-toggle v-model="coverForm.source" mandatory color="primary" variant="outlined" class="mb-3">
                <v-btn value="text">Type the text</v-btn>
                <v-btn value="pdf">Upload a PDF</v-btn>
              </v-btn-toggle>
              <v-textarea
                v-if="coverForm.source === 'text'"
                v-model="coverForm.text"
                label="Cover sheet text"
                rows="8" />
              <template v-else>
                <UploadFileComponent accept=".pdf" hide-file-name @download-link="onCoverPdfUploaded" />
                <div v-if="coverForm.fileName" class="d-flex align-center text-body-2 mt-1">
                  <v-icon size="small" class="mr-1">mdi-paperclip</v-icon>{{ coverForm.fileName }}
                </div>
                <div class="text-caption text-medium-emphasis mt-1">
                  The PDF's text is extracted once, on save, and stored as the template.
                </div>
              </template>
              <div v-if="coverDialogError" class="text-error mt-2">{{ coverDialogError }}</div>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn variant="text" :disabled="coverSaving" @click="coverDialogOpen = false">Cancel</v-btn>
              <v-btn
                color="primary"
                variant="flat"
                rounded
                :loading="coverSaving"
                :disabled="!canSaveCoverTemplate"
                @click="saveCoverTemplate">
                Save cover sheet
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <OutgoingFaxEditor
          v-if="sendMethod === 'fax'"
          :title="''"
          :order-info="order"
          :to-fax-number="''"
          :cancel-button="false"
          :appeal="true"
          :extra-file-links="documentUrls.map((d) => d.link)"
          :excluded-enclosure-links="excludedLinks"
          :excluded-enclosure-pages="excludedPages"
          :cover-sheet-text="coverSheetText"
          :send-disabled="coverFilling"
          send-button-text="Send Appeal"
          :frontend-templates="appealTemplates"
          @update:content="(v) => saveAppealField('draftText', v)"
          @on-complete="onAppealComplete">
          <template #cover>
            <AppealCoverSheetField
              :items="coverTemplateItems"
              :template-id="coverTemplateId"
              :text="coverSheetText"
              :loading="coverTemplatesLoading"
              :filling="coverFilling"
              @select="onCoverTemplateSelected"
              @update:text="onCoverTextEdited" />
          </template>
          <template #attach>
            <v-col cols="auto" class="d-flex flex-wrap align-center pa-0">
              <div v-for="(doc, i) in documentUrls" :key="doc.link" class="d-flex align-center mr-2">
                <v-icon size="small" class="mr-1">mdi-paperclip</v-icon>
                <span class="text-body-2">{{ doc.fileName || "Document" }}</span>
                <v-btn icon="mdi-close" variant="text" size="x-small" @click="removeDocument(i)" />
              </div>
              <v-btn
                tag="label"
                variant="tonal"
                color="primary"
                rounded
                prepend-icon="mdi-paperclip"
                :loading="uploading"
                :disabled="generating">
                Attach a document
                <input type="file" accept=".pdf,.tif,.tiff,.png,.jpg,.jpeg" class="d-none" @change="onPickFile" />
              </v-btn>
              <v-btn
                variant="text"
                color="primary"
                class="ml-2"
                prepend-icon="mdi-refresh"
                :loading="generating"
                @click="onGenerate">
                Regenerate
              </v-btn>
            </v-col>
          </template>
        </OutgoingFaxEditor>

        <!-- The letter loads into the body via the template dropdown; it also travels inside the packet the backend builds. -->
        <OutgoingEmailEditor
          v-else
          :title="''"
          :order-info="order"
          :cancel-button="false"
          :appeal="true"
          :appeal-attachment-links="documentUrls.map((d) => d.link)"
          :excluded-enclosure-links="excludedLinks"
          :excluded-enclosure-pages="excludedPages"
          :cover-sheet-text="coverSheetText"
          :send-disabled="coverFilling"
          send-button-text="Send Appeal"
          :frontend-templates="appealTemplates"
          @update:content="(v) => saveAppealField('draftText', v)"
          @on-complete="onAppealComplete">
          <template #cover>
            <AppealCoverSheetField
              :items="coverTemplateItems"
              :template-id="coverTemplateId"
              :text="coverSheetText"
              :loading="coverTemplatesLoading"
              :filling="coverFilling"
              @select="onCoverTemplateSelected"
              @update:text="onCoverTextEdited" />
          </template>
          <template #attach>
            <v-col cols="auto" class="d-flex flex-wrap align-center pa-0">
              <div v-for="(doc, i) in documentUrls" :key="doc.link" class="d-flex align-center mr-2">
                <v-icon size="small" class="mr-1">mdi-paperclip</v-icon>
                <span class="text-body-2">{{ doc.fileName || "Document" }}</span>
                <v-btn icon="mdi-close" variant="text" size="x-small" @click="removeDocument(i)" />
              </div>
              <v-btn
                tag="label"
                variant="tonal"
                color="primary"
                rounded
                prepend-icon="mdi-paperclip"
                :loading="uploading"
                :disabled="generating">
                Attach a document
                <input type="file" accept=".pdf,.tif,.tiff,.png,.jpg,.jpeg" class="d-none" @change="onPickFile" />
              </v-btn>
              <v-btn
                variant="text"
                color="primary"
                class="ml-2"
                prepend-icon="mdi-refresh"
                :loading="generating"
                @click="onGenerate">
                Regenerate
              </v-btn>
            </v-col>
          </template>
        </OutgoingEmailEditor>
      </template>
    </v-container>
  </v-sheet>
</template>

<script setup>
import { ref, computed, onUnmounted, onMounted } from "vue";
import { orderInfoService } from "@/services/orderInfoService";
import { eFaxService } from "@/services/eFaxService";
import { templateService } from "@/services/templateService";
import { TEMPLATE_TYPES } from "@/models/templates";
import { uuid } from "vue-uuid";
import { uploadService, validateFile, parseUploadError } from "@/services/uploadService";
import AppealCoverSheetField from "@/components/AppealCoverSheetField.vue";
import OutgoingFaxEditor from "@/components/OutgoingFaxEditor.vue";
import OutgoingEmailEditor from "@/components/OutgoingEmailEditor.vue";
import UploadFileComponent from "@/components/UploadFileComponent.vue";
import { useRequirementsStore } from "@/stores/requirementsStore";

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["appealSent"]);

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
const SAVE_DEBOUNCE_MS = 1000;
const PERSIST_DEBOUNCE_MS = 600;
const NEW_COVER_TEMPLATE = "new-cover-sheet";
const SAVED_COVER_TEMPLATE = "saved-cover-sheet";
const MAX_COVER_TEMPLATE_PAGES = 20;
const POLL_SUPERSEDED = "superseded";
const PACKET_REVIEW_FAILED =
  "Could not check this packet for blank forms or missing documents — check it before sending.";
const PACKET_REVIEW_UNAVAILABLE =
  "This packet changed and was not checked for blank forms or missing documents — check it before sending.";

const fileNameFromUrl = (url) => (url.split("/").pop() || "Document").split("?")[0];

// Seed attachments + draft from the persisted appeal letter so both survive a reload
// (and a later Regenerate re-sends the same attachments).
const documentUrls = ref(
  (props.order.appealLetter?.attachedDocumentUrls || []).map((link) => ({ link, fileName: fileNameFromUrl(link) }))
);
const uploading = ref(false);
const generating = ref(false);
const generatedText = ref(props.order.appealLetter?.draftText || "");
const errorMsg = ref("");
const autoEnclosures = ref([]);

const requirementsStore = useRequirementsStore();
const sendEmailEnabled = computed(() => requirementsStore.clientConfig?.intake?.sendEmailEnabled ?? false);
const sendMethod = ref("fax"); // 'fax' or 'email'

// Exclusions are persisted on the appeal letter so removals survive reloads and regenerates.
const excludedLinks = ref([...(props.order.appealLetter?.excludedEnclosureLinks || [])]);
const excludedPages = ref(
  (props.order.appealLetter?.excludedEnclosurePages || [])
    .filter((entry) => entry?.link && Array.isArray(entry.pages) && entry.pages.length)
    .map((entry) => ({ link: entry.link, pages: [...entry.pages] }))
);

const coverTemplates = ref([]);
const coverTemplatesLoading = ref(false);
const coverSheetText = ref(props.order.appealLetter?.coverSheetText || "");
const coverTemplateId = ref(coverSheetText.value ? SAVED_COVER_TEMPLATE : null);
const coverFilling = ref(false);
const coverDialogOpen = ref(false);
const coverSaving = ref(false);
const coverDialogError = ref("");
const coverForm = ref({ name: "", source: "text", text: "", fileLink: "", fileName: "" });

const coverTemplateItems = computed(() => [
  { title: "No cover sheet", value: null },
  ...(coverTemplateId.value === SAVED_COVER_TEMPLATE
    ? [{ title: "Saved cover sheet", value: SAVED_COVER_TEMPLATE }]
    : []),
  ...coverTemplates.value.map((template) => ({ title: template.name, value: template.id })),
  { title: "+ New cover sheet…", value: NEW_COVER_TEMPLATE },
]);

const canSaveCoverTemplate = computed(() => {
  const form = coverForm.value;
  if (!form.name.trim()) return false;
  return form.source === "text" ? !!form.text.trim() : !!form.fileLink;
});

const packetFlags = ref([...(props.order.appealLetter?.packetFlags || [])]);
const packetReviewPending = ref(false);
const packetReviewError = ref(props.order.appealLetter?.packetReviewUnavailable ? PACKET_REVIEW_UNAVAILABLE : "");

const pagesDialogOpen = ref(false);
const pagesDialogEnclosure = ref(null);
const pagesDialogInfo = ref(null);
const pagesLoading = ref(false);
const pagesError = ref("");

const visibleEnclosures = computed(() => autoEnclosures.value.filter((e) => !excludedLinks.value.includes(e.identity)));
const removedCount = computed(
  () =>
    excludedLinks.value.length + excludedPages.value.filter((entry) => !excludedLinks.value.includes(entry.link)).length
);
const dialogExcludedPages = computed(() =>
  pagesDialogEnclosure.value ? pagesForLink(pagesDialogEnclosure.value.identity) : []
);

const pagesForLink = (identity) => excludedPages.value.find((entry) => entry.link === identity)?.pages || [];

const removedPagesSuffix = (identity) => {
  const pages = pagesForLink(identity);
  if (!pages.length) return "";
  return ` · page${pages.length === 1 ? "" : "s"} ${pages.join(", ")} removed`;
};

const isPageRemoved = (page) => dialogExcludedPages.value.includes(page);

const activeFlagsFor = (identity) =>
  excludedLinks.value.includes(identity)
    ? []
    : packetFlags.value.filter(
        (flag) =>
          flag.link === identity &&
          (!flag.pages?.length || flag.pages.some((page) => !pagesForLink(identity).includes(page)))
      );

const isEnclosureFlagged = (identity) => activeFlagsFor(identity).length > 0;

const dialogFlags = computed(() =>
  pagesDialogEnclosure.value ? activeFlagsFor(pagesDialogEnclosure.value.identity) : []
);

const isPageFlagged = (page) =>
  !isPageRemoved(page) && dialogFlags.value.some((flag) => (flag.pages || []).includes(page));

const excludeEnclosure = (identity) => {
  if (identity && !excludedLinks.value.includes(identity)) {
    excludedLinks.value.push(identity);
    persistPacketState();
  }
};

const restoreEnclosures = () => {
  excludedLinks.value = [];
  excludedPages.value = [];
  persistPacketState();
};

const openPagesDialog = async (enclosure) => {
  pagesDialogEnclosure.value = enclosure;
  pagesDialogOpen.value = true;
  pagesDialogInfo.value = null;
  pagesError.value = "";
  pagesLoading.value = true;
  try {
    const info = await eFaxService.getAppealEnclosurePreview(props.order.orderKey, enclosure.link);
    if (pagesDialogEnclosure.value?.identity !== enclosure.identity) return;
    pagesDialogInfo.value = info;
  } catch (_e) {
    if (pagesDialogEnclosure.value?.identity !== enclosure.identity) return;
    pagesError.value = "Could not load this document's pages. Please try again.";
  } finally {
    if (pagesDialogEnclosure.value?.identity === enclosure.identity) pagesLoading.value = false;
  }
};

const togglePage = (page) => {
  const identity = pagesDialogEnclosure.value?.identity;
  if (!identity) return;
  const entry = excludedPages.value.find((e) => e.link === identity);
  if (!entry) {
    excludedPages.value.push({ link: identity, pages: [page] });
  } else if (entry.pages.includes(page)) {
    entry.pages = entry.pages.filter((p) => p !== page);
    if (!entry.pages.length) excludedPages.value = excludedPages.value.filter((e) => e !== entry);
  } else {
    entry.pages = [...entry.pages, page].sort((a, b) => a - b);
  }
  persistPacketState();
};

const restoreDialogPages = () => {
  const identity = pagesDialogEnclosure.value?.identity;
  if (!identity) return;
  excludedPages.value = excludedPages.value.filter((entry) => entry.link !== identity);
  persistPacketState();
};
let coverPollTimer = null;
let coverPollGeneration = 0;
let persistTimer = null;
const fieldSaveTimers = {};

const hasDraft = computed(() => !!generatedText.value);

const appealTemplates = computed(() => [
  {
    name: "AI Appeal Draft",
    content: generatedText.value,
    category: "appeal",
  },
]);

const sentInfo = computed(() => {
  const a = props.order.appealLetter;
  if (!a?.sentAt) return null;
  return {
    sentAtLabel: new Date(a.sentAt).toLocaleString(),
    sentByName: a.sentByName,
    sentDocumentUrl: a.sentDocumentUrl,
  };
});

const onPickFile = async (event) => {
  const file = event.target.files?.[0];
  if (event.target) event.target.value = "";
  if (!file) return;
  const validationError = validateFile(file);
  if (validationError) {
    errorMsg.value = validationError;
    return;
  }
  uploading.value = true;
  errorMsg.value = "";
  try {
    const link = await uploadService.uploadFile(file, `${uuid.v4()}/${file.name}`);
    documentUrls.value.push({ link, fileName: file.name });
    attachmentsVersion += 1;
    persistPacketState();
  } catch (e) {
    errorMsg.value = parseUploadError(e);
  } finally {
    uploading.value = false;
  }
};

let attachmentsVersion = 0;
let persistedAttachmentsVersion = 0;

const removeDocument = (index) => {
  documentUrls.value.splice(index, 1);
  attachmentsVersion += 1;
  persistPacketState();
};

const savePacketNow = () => {
  const payload = {
    excludedEnclosureLinks: excludedLinks.value,
    excludedEnclosurePages: excludedPages.value,
  };
  const sentVersion = attachmentsVersion;
  if (sentVersion !== persistedAttachmentsVersion) {
    payload.attachedDocumentUrls = documentUrls.value.map((d) => d.link);
  }
  return orderInfoService.saveAppealDraft(props.order.orderKey, payload).then((response) => {
    persistedAttachmentsVersion = sentVersion;
    return response;
  });
};

let packetSaveSequence = 0;
let polledReviewSequence = 0;

// Persist packet changes (debounced) so removals and attachments survive reloads and regenerates.
const persistPacketState = () => {
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    persistTimer = null;
    const sequence = ++packetSaveSequence;
    savePacketNow()
      .then((response) => trackPacketReview(response?.reviewTaskId, sequence, response?.reviewUnavailable))
      .catch((e) => {
        errorMsg.value = `Failed to save appeal changes: ${e.message || e}`;
      });
  }, PERSIST_DEBOUNCE_MS);
};

const activePolls = new Set();

const clearPoll = () => {
  activePolls.forEach((stop) => stop()); // each stop() settles its poll and removes itself, emptying the set
};

const pollAppealTask = (taskId, isCurrent = () => true) =>
  new Promise((resolve, reject) => {
    const startedAt = Date.now();
    let timer = null;
    const detach = () => {
      clearTimeout(timer);
      activePolls.delete(stop);
    };
    const stop = () => {
      detach();
      resolve({ status: POLL_SUPERSEDED });
    };
    activePolls.add(stop);
    const tick = async () => {
      try {
        const result = await orderInfoService.getAppealDraft(props.order.orderKey, taskId);
        if (!activePolls.has(stop) || !isCurrent()) {
          stop();
        } else if (result.status !== "processing") {
          detach();
          resolve(result);
        } else if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          detach();
          resolve({ status: "timeout" });
        } else {
          timer = setTimeout(tick, POLL_INTERVAL_MS);
        }
      } catch (e) {
        detach();
        reject(e);
      }
    };
    timer = setTimeout(tick, POLL_INTERVAL_MS);
  });

let packetFlagsGeneration = 0;

const assignPacketFlags = (generation, flags) => {
  if (generation === packetFlagsGeneration) packetFlags.value = flags || [];
};

const refreshPacketFlags = (reviewTaskId) => {
  const generation = ++packetFlagsGeneration;
  packetReviewPending.value = true;
  packetReviewError.value = "";
  const settle = (error) => {
    if (generation !== packetFlagsGeneration) return;
    packetReviewPending.value = false;
    packetReviewError.value = error;
  };
  return pollAppealTask(reviewTaskId, () => generation === packetFlagsGeneration)
    .then((result) => {
      if (result.status === "done") {
        assignPacketFlags(generation, result.packetFlags);
        const bound = result.reviewTaskId;
        if (bound && bound !== reviewTaskId && generation === packetFlagsGeneration) {
          refreshPacketFlags(bound);
        } else {
          settle(result.reviewUnavailable ? PACKET_REVIEW_UNAVAILABLE : "");
        }
      } else if (result.status === POLL_SUPERSEDED) {
        settle("");
      } else {
        settle(PACKET_REVIEW_FAILED);
      }
    })
    .catch((e) => {
      settle(e?.response?.status === 404 ? PACKET_REVIEW_UNAVAILABLE : PACKET_REVIEW_FAILED);
    });
};

const trackPacketReview = (reviewTaskId, sequence, reviewUnavailable) => {
  if (sequence < polledReviewSequence) return;
  if (reviewTaskId) {
    polledReviewSequence = sequence;
    refreshPacketFlags(reviewTaskId);
    return;
  }
  if (!reviewUnavailable) return;
  polledReviewSequence = sequence;
  packetFlagsGeneration += 1;
  packetReviewPending.value = false;
  packetReviewError.value = PACKET_REVIEW_UNAVAILABLE;
};

const clearCoverPoll = () => {
  coverPollGeneration += 1;
  if (coverPollTimer) {
    clearTimeout(coverPollTimer);
    coverPollTimer = null;
  }
};

const pollTask = ({ fetchStatus, registerTimer, isCurrent, labels, onDone, onFail }) => {
  const startedAt = Date.now();
  const tick = () => {
    registerTimer(
      setTimeout(async () => {
        try {
          const result = await fetchStatus();
          if (!isCurrent()) return;
          if (result.status === "done") return onDone(result);
          if (result.status === "error") return onFail(result.error || labels.failed);
          if (Date.now() - startedAt > POLL_TIMEOUT_MS) return onFail(labels.timedOut);
          tick();
        } catch (e) {
          if (!isCurrent()) return;
          onFail(`${labels.fetchFailed}: ${e.message || e}`);
        }
      }, POLL_INTERVAL_MS)
    );
  };
  tick();
};

const onGenerate = async () => {
  errorMsg.value = "";
  clearPoll();
  generating.value = true;
  let taskId;
  try {
    ({ taskId } = await orderInfoService.draftAppeal(props.order.orderKey, {
      documentUrls: documentUrls.value.map((d) => d.link),
    }));
  } catch (e) {
    generating.value = false;
    errorMsg.value = `Failed to start appeal generation: ${e.message || e}`;
    return;
  }
  const generation = ++packetFlagsGeneration;
  let result = null;
  try {
    result = await pollAppealTask(taskId);
    if (result.status === POLL_SUPERSEDED) return;
    if (result.status === "done") {
      generatedText.value = result.draftText || "";
      assignPacketFlags(generation, result.packetFlags);
      trackPacketReview(result.reviewTaskId, ++packetSaveSequence, result.reviewUnavailable);
    } else if (result.status === "timeout") {
      errorMsg.value = "Appeal drafting timed out. Please try again.";
    } else {
      errorMsg.value = result.error || "Appeal drafting failed. Please try again.";
    }
  } catch (e) {
    errorMsg.value = `Failed to fetch appeal draft: ${e.message || e}`;
  } finally {
    if (result?.status !== POLL_SUPERSEDED) generating.value = false;
  }
};

const loadCoverTemplates = async () => {
  coverTemplatesLoading.value = true;
  try {
    const loaded = [];
    for (let page = 1; page <= MAX_COVER_TEMPLATE_PAGES; page += 1) {
      const response = await templateService.getTemplates({
        templateTypes: [TEMPLATE_TYPES.APPEAL_COVER_SHEET],
        orderInfoKey: props.order.orderKey,
        page,
      });
      loaded.push(...(response?.results || []));
      if (!response?.next) break;
    }
    coverTemplates.value = loaded;
  } catch (e) {
    errorMsg.value = `Failed to load the cover sheets: ${e.message || e}`;
  } finally {
    coverTemplatesLoading.value = false;
  }
};

const startCoverFill = async (templateId) => {
  errorMsg.value = "";
  // The previous template's text must not ride along while the new fill runs or after it fails.
  coverSheetText.value = "";
  coverFilling.value = true;
  clearCoverPoll();
  const generation = coverPollGeneration;
  try {
    await orderInfoService.saveAppealDraft(props.order.orderKey, { coverSheetText: "" });
    const { taskId } = await orderInfoService.fillAppealCoverSheet(props.order.orderKey, { templateId });
    pollTask({
      fetchStatus: () => orderInfoService.getAppealCoverSheet(props.order.orderKey, taskId),
      registerTimer: (timer) => (coverPollTimer = timer),
      isCurrent: () => generation === coverPollGeneration,
      labels: {
        failed: "Filling the cover sheet failed. Please try again.",
        timedOut: "Filling the cover sheet timed out. Please try again.",
        fetchFailed: "Failed to fetch the filled cover sheet",
      },
      onDone: (result) => {
        coverSheetText.value = result.coverSheetText || "";
        coverFilling.value = false;
      },
      onFail: (message) => {
        errorMsg.value = message;
        coverFilling.value = false;
        coverTemplateId.value = null;
      },
    });
  } catch (e) {
    coverFilling.value = false;
    coverTemplateId.value = null;
    errorMsg.value = `Failed to start the cover sheet fill: ${e.message || e}`;
  }
};

// The dropdown's last item is an action rather than a template: it opens the creation dialog and
// leaves the current selection in place.
const onCoverTemplateSelected = (templateId) => {
  if (templateId === NEW_COVER_TEMPLATE) {
    openCoverDialog();
    return;
  }
  if (templateId === SAVED_COVER_TEMPLATE) return;
  clearTimeout(fieldSaveTimers.coverSheetText);
  delete fieldSaveTimers.coverSheetText;
  coverTemplateId.value = templateId;
  clearCoverPoll();
  coverFilling.value = false;
  if (!templateId) {
    // The empty option is the default: no cover sheet goes out, and an empty text also drops the
    // stored selection.
    coverSheetText.value = "";
    orderInfoService.saveAppealDraft(props.order.orderKey, { coverSheetText: "" }).catch((e) => {
      errorMsg.value = `Failed to save draft: ${e.message || e}`;
    });
    return;
  }
  startCoverFill(templateId);
};

const onCoverTextEdited = (value) => {
  coverSheetText.value = value;
  saveAppealField("coverSheetText", value);
};

const openCoverDialog = () => {
  coverForm.value = { name: "", source: "text", text: "", fileLink: "", fileName: "" };
  coverDialogError.value = "";
  coverDialogOpen.value = true;
};

const onCoverPdfUploaded = ({ link, fileName }) => {
  coverForm.value.fileLink = link;
  coverForm.value.fileName = fileName;
};

// A new company template, then straight into its fill — the user created it to use it here.
const saveCoverTemplate = async () => {
  const form = coverForm.value;
  coverDialogError.value = "";
  coverSaving.value = true;
  try {
    const payload = { orderKey: props.order.orderKey, name: form.name.trim() };
    if (form.source === "text") {
      payload.text = form.text;
    } else {
      payload.fileLink = form.fileLink;
    }
    const created = await templateService.createAppealCoverSheet(payload);
    coverDialogOpen.value = false;
    await loadCoverTemplates();
    coverTemplateId.value = created.id;
    startCoverFill(created.id);
  } catch (e) {
    coverDialogError.value = e.message || String(e);
  } finally {
    coverSaving.value = false;
  }
};

// Persist user edits to the draft (debounced) so they survive reloads.
const saveAppealField = (field, value) => {
  clearTimeout(fieldSaveTimers[field]);
  fieldSaveTimers[field] = setTimeout(() => {
    delete fieldSaveTimers[field];
    // Only the letter editor owns generatedText: a cover edit saves its own field, and writing it
    // here would put the cover text in the letter body the send dialog carries.
    if (field === "draftText") generatedText.value = value;
    orderInfoService.saveAppealDraft(props.order.orderKey, { [field]: value }).catch((e) => {
      errorMsg.value = `Failed to save draft: ${e.message || e}`;
    });
  }, SAVE_DEBOUNCE_MS);
};

const onAppealComplete = ({ sent }) => {
  if (sent) {
    emit("appealSent", { sent: true });
  }
};

onMounted(async () => {
  loadCoverTemplates();
  try {
    const res = await eFaxService.getAppealEnclosures(props.order.orderKey);
    autoEnclosures.value = res?.enclosures || [];
  } catch (_e) {
    autoEnclosures.value = []; // informational only — never block the appeal UI if it can't load
  }
});

onUnmounted(() => {
  clearPoll();
  clearCoverPoll();
  Object.values(fieldSaveTimers).forEach(clearTimeout);
  if (persistTimer) {
    clearTimeout(persistTimer);
    persistTimer = null;
    savePacketNow().catch(() => {});
  }
});
</script>

<style lang="scss" scoped>
.appeal-section {
  border: 1px solid $lh_dash_border;
  box-shadow: $lh_shadow_sm;
  border-radius: 16px !important;

  :deep(.v-btn) {
    letter-spacing: normal;
    text-transform: none;
    font-weight: 500;
  }
}

.appeal-section__intro {
  font-family: $lh_font_family_dm_sans;
  color: $lh_color_navy_dark;
}

.appeal-section__enclosure-frame {
  width: 100%;
  height: 55vh;
  border: 1px solid $lh_dash_border;
  border-radius: 8px;
}
</style>
